import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, assetUrl, thumbUrl } from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import PageHero from "@/components/PageHero";
import { useSeo } from "@/lib/seo";
import {
  WeatherCard, FactsCard, NewsList, RelatedLinks, FeaturedPhotographerCard,
} from "@/components/PlaceCards";
import {
  MapPin, PlayCircle, CaretRight, Globe, Sparkle, Images,
} from "@phosphor-icons/react";

const LEVEL_EYEBROW = { world: "Explore places", country: "Country", state: "State / Region", city: "City" };

function Breadcrumbs({ items }) {
  if (!items?.length) return null;
  return (
    <nav data-testid="loc-breadcrumbs" className="mb-4 flex flex-wrap items-center gap-1 text-sm">
      {items.map((b, i) => {
        const last = i === items.length - 1;
        return (
          <span key={b.path} className="flex items-center gap-1">
            {last ? (
              <span className="font-bold text-zinc-800">{b.label}</span>
            ) : (
              <Link to={b.path} data-testid={`loc-crumb-${i}`} className="font-semibold text-zinc-500 hover:text-[#F27A54]">{b.label}</Link>
            )}
            {!last && <CaretRight size={12} weight="bold" className="text-zinc-400" />}
          </span>
        );
      })}
    </nav>
  );
}

function IntroBlock({ intro, loading }) {
  if (loading)
    return <div data-testid="loc-intro-loading" className="mt-5 h-16 animate-pulse rounded-xl bg-zinc-100" />;
  if (!intro) return null;
  return (
    <div data-testid="loc-intro" className="mt-5 rounded-2xl border border-[#F27A54]/25 bg-[#FFF7F2] p-4 sm:p-5">
      <p className="overline flex items-center gap-1.5 text-[10px] text-[#F27A54]"><Sparkle size={13} weight="fill" /> About this place</p>
      <p className="mt-1.5 text-sm leading-relaxed text-zinc-700 sm:text-base">{intro}</p>
    </div>
  );
}

function ChildTile({ base, child }) {
  return (
    <Link to={`${base}/${child.slug}`} data-testid={`loc-child-${child.slug}`}
      className="group relative aspect-[4/3] overflow-hidden rounded-2xl border border-border bg-zinc-100 transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg">
      {child.hero_image ? (
        <img src={thumbUrl(child.hero_image)} alt={child.name} loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
      ) : (
        <div className="flex h-full w-full items-center justify-center"><MapPin size={28} className="text-zinc-300" /></div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-3">
        <p className="line-clamp-1 font-display text-base font-black text-white drop-shadow-sm">{child.name}</p>
        <p className="text-[11px] font-semibold text-white/85">{child.count} photo{child.count === 1 ? "" : "s"}</p>
      </div>
    </Link>
  );
}

function PhotoTile({ m }) {
  return (
    <Link to={m.photo_id ? `/photo/${m.photo_id}` : `/p/${m.id}`} data-testid={`loc-photo-${m.id}`}
      className="group overflow-hidden rounded-xl border border-border bg-card transition-transform duration-200 hover:-translate-y-1 hover:shadow-md">
      <div className="relative aspect-[4/3] overflow-hidden bg-zinc-100">
        <img src={thumbUrl(m.image_url)} alt={m.alt_text || m.title || "photo"} loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        {m.media_type === "video" && <PlayCircle size={38} weight="fill" className="absolute inset-0 m-auto text-white/90 drop-shadow" />}
      </div>
      <div className="p-3">
        <p className="line-clamp-1 text-sm font-bold">{m.title || "Untitled"}</p>
        {m.location?.city && <p className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground"><MapPin size={11} weight="fill" /> {m.location.city}</p>}
      </div>
    </Link>
  );
}

export default function LocationsHub() {
  const { country, state, city } = useParams();
  const [data, setData] = useState(undefined);
  const [intro, setIntro] = useState(null);
  const [introLoading, setIntroLoading] = useState(false);
  const [morePhotos, setMorePhotos] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [noMore, setNoMore] = useState(false);

  const path = ["country", country, state, city].filter((v, i) => i === 0 || v).slice(1);
  const endpoint = path.length === 0
    ? `${API}/public/locations`
    : `${API}/public/locations/${path.map(encodeURIComponent).join("/")}`;

  useEffect(() => {
    setData(undefined); setIntro(null); setMorePhotos([]); setNoMore(false);
    axios.get(endpoint).then((r) => setData(r.data)).catch(() => setData(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, state, city]);

  // Fetch AI intro once the level metadata is known.
  useEffect(() => {
    if (!data || !data.intro) return;
    setIntroLoading(true);
    const p = new URLSearchParams({
      place: data.intro.place, kind: data.intro.kind, parent: data.intro.parent || "",
    });
    axios.get(`${API}/public/location-intro?${p.toString()}`)
      .then((r) => setIntro(r.data?.intro || null))
      .catch(() => setIntro(null))
      .finally(() => setIntroLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.level, data?.name]);

  const title = data?.name || "Explore the world";
  useSeo({
    title: `${title} — Photos on PhotoPulse`,
    description: `Browse photos${data?.name ? ` from ${data.name}` : " by place"} on PhotoPulse.pro — a canonical visual library.`,
  });

  const loadMore = useCallback(async () => {
    if (!data) return;
    setLoadingMore(true);
    const shown = (data.photos?.length || 0) + morePhotos.length;
    try {
      const r = await axios.get(`${endpoint}?offset=${shown}`);
      const batch = r.data.photos || [];
      if (batch.length === 0) setNoMore(true);
      else setMorePhotos((prev) => [...prev, ...batch]);
    } finally { setLoadingMore(false); }
  }, [data, morePhotos.length, endpoint]);

  if (data === undefined)
    return (
      <PublicLayout>
        <div className="mx-auto max-w-[1200px] px-5 py-8" data-testid="loc-loading">
          <div className="h-52 animate-pulse rounded-3xl bg-zinc-100 sm:h-60" />
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-[4/3] animate-pulse rounded-2xl bg-zinc-100" />)}
          </div>
        </div>
      </PublicLayout>
    );
  if (data === null)
    return <PublicLayout><div className="mx-auto max-w-2xl px-5 py-20 text-center" data-testid="loc-notfound"><p className="text-muted-foreground">We couldn't find that place. <Link to="/photos" className="font-semibold text-[#F27A54]">Explore all places</Link>.</p></div></PublicLayout>;

  const isWorld = data.level === "world";
  const isCity = data.level === "city";
  const children = data.children || data.countries || [];
  const childBase = isWorld ? "/photos" : (data.breadcrumbs?.[data.breadcrumbs.length - 1]?.path || "/photos");
  const allPhotos = [...(data.photos || []), ...morePhotos];
  const canLoadMore = !isWorld && !noMore && allPhotos.length < (data.photo_total || 0);

  return (
    <PublicLayout>
      <div className="mx-auto max-w-[1200px] px-5 py-8">
        <Breadcrumbs items={data.breadcrumbs} />

        <PageHero
          eyebrow={LEVEL_EYEBROW[data.level] || "Places"}
          icon={isWorld ? Globe : MapPin}
          title={isWorld ? "Explore the world" : data.name}
          image={data.hero_image ? assetUrl(data.hero_image) : undefined}
          subtitle={isWorld
            ? `${data.total_photos} photos across ${data.country_count} countries`
            : `${data.count} photo${data.count === 1 ? "" : "s"}${data.country && data.level !== "country" ? ` · ${data.country}` : ""}`}
          seed={title}
        />

        <IntroBlock intro={intro} loading={introLoading} />

        {/* Child tiles: countries / states / cities */}
        {children.length > 0 && (
          <section className="mt-8">
            <h2 className="font-display text-lg font-black tracking-tight">
              {isWorld ? "Browse by country" : data.children_label}
            </h2>
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="loc-children">
              {children.map((c) => <ChildTile key={c.slug} base={childBase} child={c} />)}
            </div>
          </section>
        )}

        {/* Photo grid (country/state/city) + rich sidebar on city */}
        {!isWorld && (
          <section className="mt-8">
            <div className={isCity ? "grid gap-8 lg:grid-cols-[1fr_320px]" : ""}>
              <div>
                <h2 className="flex items-center gap-2 font-display text-lg font-black tracking-tight">
                  <Images size={18} weight="fill" className="text-[#F27A54]" /> Photos &amp; videos
                </h2>
                {allPhotos.length === 0 ? (
                  <p className="mt-4 rounded-xl border border-dashed border-border py-10 text-center text-sm text-muted-foreground" data-testid="loc-empty">
                    No published photos from {data.name} yet.
                  </p>
                ) : (
                  <>
                    <div className={`mt-4 grid gap-4 ${isCity ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"}`} data-testid="loc-photos">
                      {allPhotos.map((m) => <PhotoTile key={m.id} m={m} />)}
                    </div>
                    {canLoadMore && (
                      <div className="mt-6 text-center">
                        <button onClick={loadMore} disabled={loadingMore} data-testid="loc-load-more"
                          className="rounded-full border border-border px-6 py-2.5 text-sm font-bold transition-colors hover:border-[#F27A54] hover:text-[#F27A54] disabled:opacity-50">
                          {loadingMore ? "Loading…" : "Load more"}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>

              {isCity && (
                <aside className="space-y-5" data-testid="loc-sidebar">
                  <WeatherCard weather={data.weather} />
                  <FactsCard place={data.place} />
                  <FeaturedPhotographerCard photographer={data.featured_photographer} />
                  <NewsList news={data.news} />
                  <RelatedLinks links={data.related_links} />
                </aside>
              )}
            </div>

            {!isCity && data.featured_photographer && (
              <div className="mt-8 max-w-md" data-testid="loc-photographer-wrap">
                <FeaturedPhotographerCard photographer={data.featured_photographer} />
              </div>
            )}
          </section>
        )}
      </div>
    </PublicLayout>
  );
}
